import { Location } from "../utils/GeocoderUtility";

export class GeocodingCache {
  private memoryCache: Map<string, Location>;
  private readonly storageKey = "geocoding_cache";
  private readonly cacheDuration = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

  constructor() {
    this.memoryCache = new Map();
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const cached = localStorage.getItem(this.storageKey);
      if (cached) {
        const { entries, timestamp } = JSON.parse(cached);

        // Clear cache if it's older than cacheDuration
        if (Date.now() - timestamp > this.cacheDuration) {
          localStorage.removeItem(this.storageKey);
          return;
        }

        // Load into memory cache
        entries.forEach(([key, value]: [string, Location]) => {
          this.memoryCache.set(key, value);
        });
      }
    } catch (error) {
      console.warn("Error loading geocoding cache:", error);
    }
  }

  private saveToStorage(): void {
    try {
      const entries = Array.from(this.memoryCache.entries());
      const cache = {
        entries,
        timestamp: Date.now(),
      };
      localStorage.setItem(this.storageKey, JSON.stringify(cache));
    } catch (error) {
      console.warn("Error saving geocoding cache:", error);
    }
  }

  get(address: string): Location | undefined {
    return this.memoryCache.get(this.normalizeAddress(address));
  }

  set(address: string, location: Location): void {
    this.memoryCache.set(this.normalizeAddress(address), location);
    this.saveToStorage();
  }

  has(address: string): boolean {
    return this.memoryCache.has(this.normalizeAddress(address));
  }

  private normalizeAddress(address: string): string {
    // Remove extra spaces, convert to lowercase
    return address.toLowerCase().replace(/\s+/g, " ").trim();
  }

  clear(): void {
    this.memoryCache.clear();
    localStorage.removeItem(this.storageKey);
  }
}

export const geocodingCache = new GeocodingCache();
